import React, { useState, useMemo } from 'react';
import { useAdminContactInquiries } from '../../hooks/useAdmin';
import { ContactInquiry } from '../../types';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { 
  Mail, 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  Globe, 
  Send, 
  MessageSquare, 
  Calendar,
  X,
  User,
  Inbox
} from 'lucide-react';
import { cn } from '../../lib/utils';

export const AdminContactInquiries: React.FC = () => {
  const [page, setPage] = useState(0);
  const pageSize = 15;
  const { data, isLoading, isError, refetch } = useAdminContactInquiries(page, pageSize);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState<ContactInquiry | null>(null);

  const inquiries: ContactInquiry[] = data?.content || [];
  const totalPages: number = data?.totalPages || 1;
  const totalElements: number = data?.totalElements || inquiries.length;

  const filteredInquiries = useMemo(() => {
    if (!searchQuery.trim()) return inquiries;
    const query = searchQuery.toLowerCase();
    return inquiries.filter((inq) => 
      inq.firstName?.toLowerCase().includes(query) ||
      inq.lastName?.toLowerCase().includes(query) ||
      inq.email?.toLowerCase().includes(query) ||
      inq.type?.toLowerCase().includes(query) ||
      inq.message?.toLowerCase().includes(query)
    );
  }, [inquiries, searchQuery]);

  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
            <Inbox className="w-5 h-5 text-[#0044FF]" />
            Public Contact Inquiries
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Submissions received from the public contact form at <code className="font-mono text-xs">/contact</code>.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, org..."
              className="w-full pl-9 pr-3 py-2 text-xs bg-white dark:bg-black border-2 border-black dark:border-white rounded-none outline-none focus:ring-1 focus:ring-[#0044FF]"
            />
          </div>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => refetch()}
            className="border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 font-mono">Total Submissions</span>
            <Inbox className="w-4 h-4 text-gray-400" />
          </div>
          <div className="text-2xl font-black font-display mt-2">{totalElements}</div>
        </Card>

        <Card className="p-4 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 font-mono">Current Page Size</span>
            <User className="w-4 h-4 text-gray-400" />
          </div>
          <div className="text-2xl font-black font-display mt-2">{inquiries.length}</div>
        </Card>

        <Card className="p-4 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 font-mono">Forwarding Destination</span>
            <Mail className="w-4 h-4 text-[#0044FF]" />
          </div>
          <div className="text-sm font-bold font-mono text-[#0044FF] mt-3 truncate">contact@orgsets.com</div>
        </Card>
      </div>

      {/* Inquiries List */}
      <Card className="rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] overflow-hidden">
        {isLoading ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <Spinner size="md" />
            <p className="text-xs font-mono text-gray-500 uppercase tracking-widest">Loading contact inquiries...</p>
          </div>
        ) : isError ? (
          <div className="py-16 text-center text-red-500 font-mono text-sm">
            Failed to load contact inquiries from backend.
          </div>
        ) : filteredInquiries.length === 0 ? (
          <div className="py-20 text-center text-gray-500">
            <Inbox className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="font-bold">No contact inquiries found.</p>
            <p className="text-xs text-gray-400 mt-1">
              {searchQuery ? 'Try adjusting your search query.' : 'Inquiries submitted through /contact will show here.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {filteredInquiries.map((inquiry) => {
              const fullName = `${inquiry.firstName || ''} ${inquiry.lastName || ''}`.trim() || 'Anonymous';
              const createdDate = inquiry.createdAt ? new Date(inquiry.createdAt).toLocaleString() : 'Unknown';

              return (
                <div 
                  key={inquiry.id}
                  className="p-5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-4"
                  onClick={() => setSelectedInquiry(inquiry)}
                >
                  <div className="space-y-1.5 flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-gray-900 dark:text-white text-base">
                        {fullName}
                      </span>
                      <Badge variant="blue" className="rounded-none text-[10px] font-mono uppercase tracking-wider font-bold">
                        {inquiry.type || 'General'}
                      </Badge>
                      <span className="text-xs text-gray-400 flex items-center gap-1 font-mono">
                        <Clock className="w-3 h-3" />
                        {createdDate}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600 dark:text-gray-300">
                      <span className="flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-[#0044FF]" />
                        <span className="font-mono">{inquiry.email}</span>
                      </span>
                      {inquiry.ipAddress && (
                        <span className="flex items-center gap-1.5 text-gray-400 font-mono">
                          <Globe className="w-3.5 h-3.5" />
                          {inquiry.ipAddress}
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                      {inquiry.message}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <a
                      href={`mailto:${inquiry.email}?subject=Re: OrgSets Inquiry (${encodeURIComponent(inquiry.type || 'General')})`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-black text-white hover:bg-[#0044FF] border border-black dark:bg-white dark:text-black dark:border-white transition-colors font-mono"
                    >
                      <Send className="w-3 h-3" />
                      Reply
                    </a>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setSelectedInquiry(inquiry)}
                      className="text-xs font-mono"
                    >
                      View
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination Bar */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50 dark:bg-black">
            <span className="text-xs font-mono text-gray-500">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Prev
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Inquiry Detail Modal */}
      {selectedInquiry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="relative w-full max-w-2xl bg-white dark:bg-[#0A0A0A] border-4 border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#FFFFFF] p-6 space-y-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between border-b pb-4">
              <div>
                <Badge variant="blue" className="rounded-none uppercase font-mono tracking-wider font-bold mb-2">
                  {selectedInquiry.type || 'General'}
                </Badge>
                <h3 className="text-xl font-black font-display text-gray-900 dark:text-white">
                  {selectedInquiry.firstName} {selectedInquiry.lastName}
                </h3>
                <p className="text-xs font-mono text-gray-400 mt-0.5">
                  Submitted {new Date(selectedInquiry.createdAt).toLocaleString()}
                </p>
              </div>
              <button 
                onClick={() => setSelectedInquiry(null)}
                className="p-1.5 border-2 border-black dark:border-white hover:bg-[#FFD600] hover:text-black transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-mono bg-gray-50 dark:bg-white/5 p-4 border border-gray-200 dark:border-gray-800">
              <div>
                <span className="text-gray-400 block mb-1">Email:</span>
                <a href={`mailto:${selectedInquiry.email}`} className="text-[#0044FF] underline font-bold">
                  {selectedInquiry.email}
                </a>
              </div>
              <div>
                <span className="text-gray-400 block mb-1">IP Address:</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {selectedInquiry.ipAddress || 'Not recorded'}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 font-mono">
                Message Content
              </label>
              <div className="p-4 bg-gray-50 dark:bg-white/5 border-2 border-black/10 dark:border-white/10 text-sm whitespace-pre-wrap leading-relaxed text-gray-800 dark:text-gray-200">
                {selectedInquiry.message}
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedInquiry(null)}
              >
                Close
              </Button>
              <a
                href={`mailto:${selectedInquiry.email}?subject=Re: OrgSets Inquiry (${encodeURIComponent(selectedInquiry.type || 'General')})`}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-bold uppercase tracking-wider bg-black text-white hover:bg-[#0044FF] border-2 border-black dark:bg-white dark:text-black dark:border-white transition-colors font-mono"
              >
                <Send className="w-4 h-4" />
                Reply to {selectedInquiry.firstName}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
